import React, { useState, useEffect, useRef } from "react";
import SupportEmail from "../components/SupportEmail";
import "../styles/waitlist-styles.css";
import "../styles/mobile-styles.css";

const Mobile = () => {
  const [step, setStep] = useState('form'); // 'form', 'success', 'error'
  const [formData, setFormData] = useState({ name: 'User' }); // Default name since nickname is hidden
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationResult, setRegistrationResult] = useState({
    loginId: '',
    recoveryKey: ''
  });
  const [fingerprintLoaded, setFingerprintLoaded] = useState(false);
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);
  
  // Turnstile states
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileWidgetId, setTurnstileWidgetId] = useState(null);
  const turnstileRef = useRef(null);
  const scriptLoadedRef = useRef(false);

  // Manual human-verification checkbox (must be clicked by a real user;
  // guarantees interaction even when Turnstile auto-solves silently on mobile)
  const [humanChecked, setHumanChecked] = useState(false);
  const [humanCheckedAt, setHumanCheckedAt] = useState(0);
  const humanCheckRef = useRef(null);

  const API_BASE_URL = 'https://1geg9kws47.execute-api.us-east-1.amazonaws.com/default/OmegaVoidWaitlist';
  const TURNSTILE_SITE_KEY = '0x4AAAAAAB40n8pyMd1635zx';

  // Fallback basic fingerprint generation function
  const generateBasicFingerprint = () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Omega Void Device ID', 2, 2);
      
      const fingerprint = [
        navigator.userAgent,
        navigator.language,
        window.screen.width + 'x' + window.screen.height,
        window.screen.colorDepth,
        new Date().getTimezoneOffset(),
        !!window.sessionStorage,
        !!window.localStorage,
        canvas.toDataURL()
      ].join('|');
      
      // Simple hash function
      let hash = 0;
      for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      
      return 'fallback_' + Math.abs(hash).toString(16);
    } catch (error) {
      console.error('Error generating fallback fingerprint:', error);
      return 'fallback_' + Date.now().toString(16);
    }
  };

  // Load FingerprintJS and generate device fingerprint
  useEffect(() => {
    const loadFingerprintJS = async () => {
      try {
        // Load FingerprintJS from CDN
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@fingerprintjs/fingerprintjs@3/dist/fp.min.js';
        
        script.onload = async () => {
          try {
            // Initialize FingerprintJS
            const fp = await window.FingerprintJS.load();
            
            // Get the visitor identifier
            const result = await fp.get();
            
            setDeviceFingerprint({
              visitorId: result.visitorId,
              confidence: result.confidence,
              components: {
                // Store key components for additional entropy
                userAgent: result.components.userAgent?.value || 'unknown',
                language: result.components.language?.value || 'unknown',
                screen: result.components.screenResolution?.value || 'unknown',
                timezone: result.components.timezone?.value || 'unknown',
                platform: result.components.platform?.value || 'unknown',
                cookiesEnabled: result.components.cookiesEnabled?.value || false,
                localStorageEnabled: result.components.localStorageEnabled?.value || false,
                canvas: result.components.canvas?.value || 'unknown'
              }
            });
            
            setFingerprintLoaded(true);
            console.log('Device fingerprint generated:', result.visitorId);
          } catch (fpError) {
            console.error('Error generating fingerprint:', fpError);
            // Fallback to basic fingerprint if FingerprintJS fails
            const fallbackFingerprint = generateBasicFingerprint();
            setDeviceFingerprint({
              visitorId: fallbackFingerprint,
              confidence: 0.5,
              components: { fallback: true }
            });
            setFingerprintLoaded(true);
          }
        };
        
        script.onerror = () => {
          console.error('Failed to load FingerprintJS, using fallback');
          const fallbackFingerprint = generateBasicFingerprint();
          setDeviceFingerprint({
            visitorId: fallbackFingerprint,
            confidence: 0.3,
            components: { fallback: true }
          });
          setFingerprintLoaded(true);
        };
        
        document.head.appendChild(script);
      } catch (error) {
        console.error('Error loading fingerprinting:', error);
        const fallbackFingerprint = generateBasicFingerprint();
        setDeviceFingerprint({
          visitorId: fallbackFingerprint,
          confidence: 0.3,
          components: { fallback: true }
        });
        setFingerprintLoaded(true);
      }
    };

    loadFingerprintJS();
  }, []);

  // Load Cloudflare Turnstile
  useEffect(() => {
    // Prevent loading script multiple times
    if (scriptLoadedRef.current) {
      return;
    }

    const loadTurnstile = () => {
      // Check if Turnstile is already loaded
      if (window.turnstile) {
        setTurnstileLoaded(true);
        scriptLoadedRef.current = true;
        return;
      }

      // Check if script already exists in DOM
      const existingScript = document.querySelector('script[src*="turnstile"]');
      if (existingScript) {
        // Wait for it to load
        const checkTurnstile = setInterval(() => {
          if (window.turnstile) {
            setTurnstileLoaded(true);
            scriptLoadedRef.current = true;
            clearInterval(checkTurnstile);
          }
        }, 100);
        
        // Clear interval after 10 seconds to prevent infinite loop
        setTimeout(() => clearInterval(checkTurnstile), 10000);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        console.log('Turnstile loaded successfully');
        setTurnstileLoaded(true);
        scriptLoadedRef.current = true;
      };
      
      script.onerror = (e) => {
        console.error('Failed to load Turnstile:', e);
        setError('Security verification failed to load. Please refresh the page.');
      };
      
      document.head.appendChild(script);
    };

    loadTurnstile();

    // Cleanup function
    return () => {
      // Note: We don't remove the script on unmount as it might be needed again
    };
  }, []);

  // Render Turnstile widget when ready
  useEffect(() => {
    // Only render if:
    // 1. Turnstile is loaded
    // 2. We have a ref to attach to
    // 3. We haven't already rendered a widget
    // 4. We're on the form step
    if (!turnstileLoaded || !turnstileRef.current || turnstileWidgetId !== null || step !== 'form') {
      return;
    }

    // Add a small delay to ensure DOM is fully ready
    const timer = setTimeout(() => {
      try {
        if (!window.turnstile) {
          console.error('Turnstile not available on window object');
          return;
        }

        if (!turnstileRef.current) {
          console.error('Turnstile ref not available');
          return;
        }

        const widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'light',
          size: 'normal',
          appearance: 'always',
          execution: 'render',
          'refresh-expired': 'manual',
          callback: (token) => {
            console.log('Turnstile token received');
            setTurnstileToken(token);
          },
          'error-callback': (error) => {
            console.error('Turnstile error:', error);
            setTurnstileToken(null);
          },
          'expired-callback': () => {
            console.log('Turnstile token expired');
            setTurnstileToken(null);
          },
          'timeout-callback': () => {
            console.log('Turnstile timeout');
            setTurnstileToken(null);
          }
        });
        
        setTurnstileWidgetId(widgetId);
        console.log('Turnstile widget rendered with ID:', widgetId);
      } catch (error) {
        console.error('Error rendering Turnstile widget:', error);
        setError('Failed to initialize security verification. Please refresh the page.');
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [turnstileLoaded, step, turnstileWidgetId]);

  // Cleanup widget on unmount or step change
  useEffect(() => {
    return () => {
      if (window.turnstile && turnstileWidgetId !== null) {
        try {
          window.turnstile.remove(turnstileWidgetId);
        } catch (e) {
          console.error('Error removing Turnstile widget:', e);
        }
      }
    };
  }, [turnstileWidgetId]);

  // Reset Turnstile widget when needed
  const resetTurnstile = () => {
    if (window.turnstile && turnstileWidgetId !== null) {
      try {
        window.turnstile.reset(turnstileWidgetId);
        setTurnstileToken(null);
      } catch (error) {
        console.error('Error resetting Turnstile:', error);
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Nickname validation disabled - using default value
      // if (!formData.name.trim()) {
      //   throw new Error('Please enter a Nickname');
      // }
      // if (formData.name.trim().length < 2) {
      //   throw new Error('Name must be at least 2 characters long');
      // }

      // Wait for fingerprint to be loaded
      if (!fingerprintLoaded) {
        throw new Error('Device verification in progress. Please wait a moment and try again.');
      }

      if (!deviceFingerprint) {
        throw new Error('Device verification failed. Please refresh the page and try again.');
      }

      // Require the manual human checkbox (prevents automated submissions
      // even if Turnstile auto-solves on mobile browsers)
      if (!humanChecked || !humanCheckedAt) {
        throw new Error('Please tick the "I am human" box to continue');
      }

      // Validate Turnstile token
      if (!turnstileToken) {
        throw new Error('Please complete the security verification');
      }

      // Prepare payload with device fingerprint and turnstile token
      const payload = {
        name: formData.name.trim(),
        source: 'mobile',
        turnstile_token: turnstileToken,
        device_fingerprint: {
          visitor_id: deviceFingerprint.visitorId,
          confidence_score: deviceFingerprint.confidence,
          fingerprint_version: 'fingerprintjs_v3',
          components: deviceFingerprint.components,
          timestamp: Date.now()
        }
      };

      // Submit to Lambda function via API Gateway
      const response = await fetch(`${API_BASE_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 429) {
          throw new Error(errorData.message || 'Registration limit reached. Please try again later.');
        }
        
        if (response.status === 403) {
          throw new Error('Security verification failed. Please try again.');
        }
        
        throw new Error(errorData.error || 'Failed to register. Please try again.');
      }

      const result = await response.json();
      
      // Expecting API to return { loginId: 'xxx', recoveryKey: 'xxx' }
      if (!result.loginId || !result.recoveryKey) {
        throw new Error('Registration completed but credentials not received. Please contact support.');
      }

      setRegistrationResult({
        loginId: result.loginId,
        recoveryKey: result.recoveryKey
      });

      setStep('success');
    } catch (err) {
      setError(err.message);
      setStep('error');
      resetTurnstile(); // Reset captcha on error
      // Force user to re-tick the "I am human" box on any failure
      setHumanChecked(false);
      setHumanCheckedAt(0);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log(`${type} copied to clipboard`);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const renderRegistrationForm = () => (
    <div className="mobile-registration-section highlight">
      <div className="mobile-hero">
        <h1>🚀 Get Instant Access</h1>
        <p className="mobile-subtitle">
          Create your Omega Void account in seconds - no email required
        </p>
      </div>

      <div className="mobile-features">
        <div className="feature-item">
          <span className="feature-icon">⚡</span>
          <span>Instant Access</span>
        </div>
        <div className="feature-item">
          <span className="feature-icon">🔒</span>
          <span>Minimal Data Retention</span>
        </div>
        {/* <div className="feature-item">
          <span className="feature-icon">🆓</span>
          <span>4 Months Free</span>
        </div> */}
        <div className="feature-item">
          <span className="feature-icon">🎫</span>
          <span>Daily Tokens</span>
        </div>
        <div className="feature-item">
          <span className="feature-icon">🔐</span>
          <span>End-to-End Encrypted</span>
        </div>
      </div>

      <div className="registration-form">
        <form onSubmit={handleFormSubmit}>
          {/* Nickname field hidden - using auto-generated value */}
          {/* <div className="form-group">
            <label htmlFor="register-name">Your Nickname *</label>
            <input
              type="text"
              id="register-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Enter a Nickname"
              className="form-input"
              maxLength="50"
            />
            <div className="input-hint">
              Only a Nickname is required - no email, no verification needed
            </div>
          </div> */}

          {/* Manual human-verification checkbox — must be clicked by a real user.
              Cloudflare Turnstile in Managed mode can auto-solve on mobile browsers,
              so this explicit interaction guarantees a human is present. */}
          <div className="form-group">
            <label
              htmlFor="human-check"
              onClick={(e) => {
                // Ignore any click that isn't from a real user gesture
                if (!e.isTrusted) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '14px 18px',
                margin: '10px auto',
                maxWidth: '320px',
                border: '2px solid #d0d7de',
                borderRadius: '10px',
                background: humanChecked ? '#eaf7ee' : '#fafbfc',
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: '15px',
                fontWeight: 500,
                color: '#24292f',
                transition: 'background 0.15s ease, border-color 0.15s ease'
              }}
            >
              <input
                ref={humanCheckRef}
                type="checkbox"
                id="human-check"
                checked={humanChecked}
                onChange={(e) => {
                  // Only accept a state change that came from a real user gesture
                  if (!e.nativeEvent || !e.nativeEvent.isTrusted) {
                    e.preventDefault();
                    setHumanChecked(false);
                    setHumanCheckedAt(0);
                    return;
                  }
                  const next = e.target.checked;
                  setHumanChecked(next);
                  setHumanCheckedAt(next ? Date.now() : 0);
                }}
                style={{
                  width: '22px',
                  height: '22px',
                  cursor: 'pointer',
                  accentColor: '#2b8a3e'
                }}
              />
              <span>I am human</span>
            </label>
          </div>

          {/* Cloudflare Turnstile Widget */}
          <div className="form-group">
            <div
              ref={turnstileRef}
              className="cf-turnstile"
              style={{
                marginTop: '20px',
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'center',
                minHeight: '65px'
              }}
            ></div>
            {!turnstileLoaded && (
              <div style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
                Loading security verification...
              </div>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            disabled={loading || !fingerprintLoaded || !humanChecked || !turnstileToken}
            className="register-button"
          >
            {loading ? (
              <span className="loading-spinner">⏳ Creating Account...</span>
            ) : !fingerprintLoaded ? (
              <span className="loading-spinner">🔐 Verifying Device...</span>
            ) : !humanChecked ? (
              <span className="loading-spinner">☑️ Tick the "I am human" box</span>
            ) : !turnstileToken ? (
              <span className="loading-spinner">🛡️ Complete Security Check...</span>
            ) : (
              'Create Account Now'
            )}
          </button>
        </form>

        <div className="registration-benefits">
          <h3>What You Get Instantly:</h3>
          <ul className="benefits-list">
            <li>✅ Immediate login credentials</li>
            <li>✅ Completely free access</li>
            <li>✅ Daily token allocation for messaging</li>
            <li>✅ All premium features included</li>
            <li>✅ No email verification required</li>
            <li>✅ Recovery key for account security</li>
            <li>✅ Minimal metadata collection</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="mobile-registration-section success">
      <div className="mobile-hero">
        <div className="success-icon">🎉</div>
        <h1>Welcome to Omega Void!</h1>
        <p className="success-subtitle">
          Your account has been created successfully. Save your credentials below!
        </p>
      </div>
      
      <div className="success-content">
        <div className="credentials-section">
          <h3>🔑 Your Login Credentials</h3>
          <p className="credentials-warning">
            <strong>⚠️ Important:</strong> Save these credentials immediately. 
            They cannot be recovered if lost!
          </p>
          
          <div className="credential-item">
            <label>Login ID:</label>
            <div className="credential-display">
              <code className="credential-code">{registrationResult.loginId}</code>
              <button 
                className="copy-button"
                onClick={() => copyToClipboard(registrationResult.loginId, 'Login ID')}
                title="Copy Login ID"
              >
                📋 Copy
              </button>
            </div>
          </div>

          <div className="credential-item">
            <label>Recovery Key:</label>
            <div className="credential-display">
              <code className="credential-code">{registrationResult.recoveryKey}</code>
              <button 
                className="copy-button"
                onClick={() => copyToClipboard(registrationResult.recoveryKey, 'Recovery Key')}
                title="Copy Recovery Key"
              >
                📋 Copy
              </button>
            </div>
          </div>
        </div>
        
        <div className="next-steps">
          <h4>What to Do Next:</h4>
          <div className="steps-grid">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-content">
                <h5>Save Your Credentials</h5>
                <p>Store your Login ID and Recovery Key in a secure location</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-content">
                <h5>Start Using Omega Void</h5>
                <p>Use your Login ID to access the platform immediately</p>
              </div>
            </div>
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-content">
                <h5>Enjoy Complete Privacy</h5>
                <p>Experience communication with minimal data retention</p>
              </div>
            </div>
          </div>
        </div>

        <div className="security-notice">
          <h4>🛡️ Security & Usage Notice</h4>
          <p>
            Your Recovery Key is the only way to regain access if you lose your Login ID. 
            We cannot help recover lost credentials as we don't store any personal information.
          </p>
          <p style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
            <strong>Daily Tokens:</strong> Your account includes a daily allocation of tokens for messaging. 
            Tokens reset every 24 hours and ensure fair usage across our networks.
          </p>
        </div>

        <div className="action-buttons">
          <button 
            className="primary-action-button"
            onClick={() => {
              // You could redirect to the main app here
              console.log('Redirect to main app');
            }}
          >
            🚀 Start Using Omega Void
          </button>
          
          <button
            className="secondary-action-button"
            onClick={() => {
              setStep('form');
              setFormData({ name: 'User' });
              setRegistrationResult({ loginId: '', recoveryKey: '' });
              setError('');
              setTurnstileToken(null);
              setTurnstileWidgetId(null);
              setHumanChecked(false);
              setHumanCheckedAt(0);
            }}
          >
            Create Another Account
          </button>
        </div>
      </div>
    </div>
  );

  const renderError = () => (
    <div className="mobile-registration-section error">
      <div className="mobile-hero">
        <div className="error-icon">❌</div>
        <h1>Registration Failed</h1>
      </div>
      
      <div className="error-content">
        <h3>Something went wrong</h3>
        <p>{error}</p>
        <p>Don't worry, you can try creating your account again.</p>
        
        <button
          onClick={() => {
            setStep('form');
            setError('');
            setTurnstileToken(null);
            setTurnstileWidgetId(null);
            setHumanChecked(false);
            setHumanCheckedAt(0);
          }}
          className="retry-button"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 'form':
        return renderRegistrationForm();
      case 'success':
        return renderSuccess();
      case 'error':
        return renderError();
      default:
        return renderRegistrationForm();
    }
  };

  return (
    <>
      <div className="mobile-container">
        {renderCurrentStep()}
        
        <div className="mobile-info-section">
          <h2>Why Choose Omega Void?</h2>
          <div className="info-grid">
            <div className="info-card">
              <div className="info-icon">🛡️</div>
              <h3>Message Privacy </h3>
              <p>Encrypted message blobs & encrypted omega links.</p>
            </div>
            <div className="info-card">
              <div className="info-icon">⚡</div>
              <h3>Lightning Fast</h3>
              <p>Instant account creation and message delivery with end-to-end encryption. Daily token system ensures optimal performance.</p>
            </div>
            <div className="info-card">
              <div className="info-icon">🌍</div>
              <h3>Global Access</h3>
              <p>Connect with anyone, anywhere in the world through our secure communication grid.</p>
            </div>
            <div className="info-card">
              <div className="info-icon">🔓</div>
              <h3>No Personal Data</h3>
              <p>No email required, no verification process - just instant access to private communication.</p>
            </div>
          </div>
        </div>

      </div>
      <SupportEmail />
    </>
  );
};

export default Mobile;